import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Fade
} from '@mui/material';
import { Save, Visibility, VisibilityOff, CheckCircle, Download, CloudDownload, FlashOn as FlashOnIcon, Schedule as ScheduleIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import ToggleSwitch from '../backend/ToggleSwitch';
import useMappedInLocations from '../../hooks/useMappedInLocations';
import { useMappedInSettings } from '../../hooks/useMappedInSettings';
import { fetchStepsFromMappedIn } from '../../utils/mappedInSteps';
import { getMappedInDirections, bulkGetDirectionsFromStart } from '../../services/mappedInDirectionsService';

const MapSettings = ({ userId, buttonColor = '#000000' }) => {
  const [settings, setSettings] = useState({
    apiKey: '',
    apiSecret: '',
    mapId: '',
    enabled: false,
    usePointrMap: false,
    startLocation: '',
    mappedInStartLocation: '',
    mappedInStartLocationName: '',
    mapLink: '',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingCategories, setImportingCategories] = useState(false);
  const [importingHierarchical, setImportingHierarchical] = useState(false);
  const [importingTags, setImportingTags] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [tagImportDialogOpen, setTagImportDialogOpen] = useState(false);
  const [tagImportResults, setTagImportResults] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [categoryImportDialogOpen, setCategoryImportDialogOpen] = useState(false);
  const [categoryImportResults, setCategoryImportResults] = useState(null);
  const [hierarchicalImportDialogOpen, setHierarchicalImportDialogOpen] = useState(false);
  const [hierarchicalImportResults, setHierarchicalImportResults] = useState(null);

  // Steps calculation state
  const [calculatingSteps, setCalculatingSteps] = useState(false);
  const [stepsProgress, setStepsProgress] = useState(0);
  const [stepsStatus, setStepsStatus] = useState('');
  const [stepsDialogOpen, setStepsDialogOpen] = useState(false);
  const [stepsResults, setStepsResults] = useState(null);

  // Import steps for locations state
  const [importingSteps, setImportingSteps] = useState(false);
  const [importStepsProgress, setImportStepsProgress] = useState(0);
  const [importStepsStatus, setImportStepsStatus] = useState('');
  const [importStepsDialogOpen, setImportStepsDialogOpen] = useState(false);
  const [importStepsResults, setImportStepsResults] = useState(null);

  // Generate text directions state
  const [generatingTextDirections, setGeneratingTextDirections] = useState(false);
  const [textDirectionsProgress, setTextDirectionsProgress] = useState(0);
  const [textDirectionsStatus, setTextDirectionsStatus] = useState('');
  const [textDirectionsDialogOpen, setTextDirectionsDialogOpen] = useState(false);
  const [textDirectionsResults, setTextDirectionsResults] = useState(null);

  // Video generation modal state for text directions
  const [videoGenModal, setVideoGenModal] = useState({ open: false, locationsToProcess: [] });
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);

  // Bulk fill text directions state (for the "Fill Empty Text Directions" button)
  const [importingTextDirections, setImportingTextDirections] = useState(false);

  // Unified bulk import state (with persistence)
  const [bulkImporting, setBulkImporting] = useState(() => {
    const saved = localStorage.getItem(`bulkImport_${userId}_active`);
    return saved === 'true';
  });
  const [bulkImportJobId, setBulkImportJobId] = useState(() => {
    return localStorage.getItem(`bulkImport_${userId}_jobId`) || null;
  });
  const [bulkImportProgress, setBulkImportProgress] = useState(() => {
    const saved = localStorage.getItem(`bulkImport_${userId}_progress`);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [bulkImportStatus, setBulkImportStatus] = useState(() => {
    return localStorage.getItem(`bulkImport_${userId}_status`) || '';
  });
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);

  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);
  // Fetch MappedIn locations using the custom hook
  const { locations: mappedInLocations, categories: mappedInCategories, loading: locationsLoading, error: locationsError, loadMappedInAndFetchData, setInitialized } = useMappedInLocations(userId, mappedInConfig, configLoading);

  // Resume polling if there's an active job when component mounts
  useEffect(() => {
    if (bulkImporting && bulkImportJobId && bulkImportProgress < 100) {
      console.log('Resuming bulk import polling for job:', bulkImportJobId);
      startPollingBulkImport(bulkImportJobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Fetch settings on component mount
  useEffect(() => {
    if (userId) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Debug: Log settings changes
  useEffect(() => {
    console.log('Settings updated:', settings);
  }, [settings]);

  // Debug: Log locations changes
  useEffect(() => {
    console.log('MappedIn locations updated:', mappedInLocations?.length || 0, 'locations');
    if (settings.mappedInStartLocation && mappedInLocations && mappedInLocations.length > 0) {
      const found = mappedInLocations.find(loc => (loc.externalId || loc.id) === settings.mappedInStartLocation);
      console.log('Selected location found:', found ? found.name : 'NOT FOUND');
    }
  }, [mappedInLocations, settings.mappedInStartLocation]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log('Fetching MappedIn settings for userId:', userId);
      const response = await axios.get(`/api/wayfinder/mappedin-settings/${userId}`);
      console.log('MappedIn settings response:', response.data);

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching MappedIn settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load MappedIn settings',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    console.log(`Field ${field} changed to:`, value);
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear message when user starts editing
    setMessage({ type: '', text: '' });
  };

  const handleToggleEnabled = (isChecked) => {
    console.log('Toggle changed:', isChecked);
    setSettings(prev => ({
      ...prev,
      enabled: isChecked,
    }));
    // Clear message when toggling
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      console.log('Saving settings:', settings);

      // Validate if enabled
      if (settings.enabled && (!settings.apiKey || !settings.apiSecret || !settings.mapId)) {
        setMessage({
          type: 'error',
          text: 'Please fill in all fields when MappedIn is enabled',
        });
        setSaving(false);
        return;
      }

      const response = await axios.post(`/api/wayfinder/mappedin-settings/${userId}`, settings);
      console.log('Save response:', response.data);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'MappedIn settings saved successfully!',
        });

        // Update local settings state with saved data
        setSettings(response.data.data);
        
        // Trigger re-fetch of MappedIn data if settings are enabled
        if (response.data.data.enabled && response.data.data.apiKey && response.data.data.apiSecret && response.data.data.mapId) {
          // Force reload of MappedIn locations with new credentials
          setInitialized(false);
          setTimeout(() => {
            loadMappedInAndFetchData();
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error saving MappedIn settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save MappedIn settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImportCategories = async () => {
    try {
      setImportingCategories(true);
      setMessage({ type: '', text: '' });

      if (!mappedInCategories || mappedInCategories.length === 0) {
        setMessage({
          type: 'error',
          text: 'No MappedIn categories found to import. Please check your API settings.',
        });
        setImportingCategories(false);
        return;
      }

      console.log('Importing categories:', mappedInCategories);

      const response = await axios.post(`/api/wayfinder/categories/bulk-import-mappedin/${userId}`, {
        categories: mappedInCategories,
      });

      console.log('Category import response:', response.data);

      if (response.data.success) {
        setCategoryImportResults(response.data.data);
        setCategoryImportDialogOpen(true);
        setMessage({
          type: 'success',
          text: response.data.message,
        });
      }
    } catch (error) {
      console.error('Error importing categories:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to import categories from MappedIn',
      });
    } finally {
      setImportingCategories(false);
    }
  };

  const handleCloseCategoryImportDialog = () => {
    setCategoryImportDialogOpen(false);
    setCategoryImportResults(null);
  };

  const handleImportCategoriesWithLocations = async () => {
    try {
      setImportingHierarchical(true);
      setImportProgress(0);
      setImportStatus('Initializing import...');
      setMessage({ type: '', text: '' });

      if (!mappedInCategories || mappedInCategories.length === 0) {
        setMessage({
          type: 'error',
          text: 'No MappedIn categories found to import. Please check your API settings.',
        });
        setImportingHierarchical(false);
        return;
      }

      // Create axios instance with longer timeout for large imports
      const axiosWithTimeout = axios.create({
        timeout: 600000, // 10 minutes timeout for large imports
      });

      setImportProgress(5);
      setImportStatus('Fetching categories with locations from MappedIn...');
      console.log('Fetching categories with locations from MappedIn...');

      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;

      const mapData = await getMapData(mappedInConfig);
      const enterpriseCategories = mapData.getByType('enterprise-category');
      const enterpriseLocations = mapData.getByType('enterprise-location');

      setImportProgress(10);
      setImportStatus('Building category structure...');

      // ── 1. Build categories with locations ──────────────────────────────────
      const categoriesWithLocations = enterpriseCategories
        .map(category => {
          const sortedLocations = category.locations 
            ? [...category.locations]
                .filter(loc => loc.name && loc.name.trim() !== '')
                .map(loc => ({
                  id: loc.id,
                  name: loc.name.trim(),
                  description: loc.description || '',
                  externalId: loc.externalId || '',
                  floor: loc.floor?.name || loc.floor?.id || '',
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
            : [];

          return {
            id: category.id,
            name: category.name.trim(),
            description: category.description || '',
            externalId: category.externalId || '',
            locations: sortedLocations,
            locationCount: sortedLocations.length,
          };
        })
        .filter(cat => cat.name && cat.name.trim() !== '')
        .sort((a, b) => a.name.localeCompare(b.name));

      const totalLocations = categoriesWithLocations.reduce((sum, cat) => sum + cat.locations.length, 0);
      console.log(`Categories with locations: ${categoriesWithLocations.length} categories, ${totalLocations} locations`);

      setImportProgress(15);
      setImportStatus(`Importing ${categoriesWithLocations.length} categories and ${totalLocations} locations...`);

      // ── 2. Import categories + locations using CHUNKED approach (by location count) ─────
      const LOCATIONS_PER_CHUNK = 50; // Process 50 locations at a time
      const chunks = [];
      let currentChunk = [];
      let currentLocationCount = 0;

      // Distribute categories into chunks based on location count
      for (const category of categoriesWithLocations) {
        const categoryLocationCount = category.locations?.length || 0;
        
        // If adding this category would exceed the limit and we have items in current chunk
        if (currentLocationCount + categoryLocationCount > LOCATIONS_PER_CHUNK && currentChunk.length > 0) {
          chunks.push(currentChunk);
          currentChunk = [category];
          currentLocationCount = categoryLocationCount;
        } else {
          currentChunk.push(category);
          currentLocationCount += categoryLocationCount;
        }
      }

      // Add the last chunk if it has items
      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      console.log(`📦 Split into ${chunks.length} chunks (~${LOCATIONS_PER_CHUNK} locations per chunk)`);
      console.log(`📊 Chunk distribution:`, chunks.map((chunk, i) => {
        const locCount = chunk.reduce((sum, cat) => sum + (cat.locations?.length || 0), 0);
        return `Chunk ${i + 1}: ${chunk.length} categories, ${locCount} locations`;
      }));

      // Initialize chunked import session
      const initResponse = await axiosWithTimeout.post(
        `/api/wayfinder/categories/bulk-import-with-locations/${userId}/init`,
        {
          totalChunks: chunks.length,
          totalCategories: categoriesWithLocations.length,
        }
      );

      const sessionId = initResponse.data.sessionId;
      console.log(`🔑 Session ID: ${sessionId}`);

      // Process each chunk
      let combinedResults = {
        categories: { created: [], skipped: [], errors: [] },
        locations: { created: [], skipped: [], errors: [] },
        totalCategories: 0,
        totalLocations: 0,
      };

      for (let i = 0; i < chunks.length; i++) {
        const chunkLocationCount = chunks[i].reduce((sum, cat) => sum + (cat.locations?.length || 0), 0);
        const chunkProgress = 15 + Math.floor((i / chunks.length) * 35); // 15% to 50%
        setImportProgress(chunkProgress);
        setImportStatus(`Processing chunk ${i + 1}/${chunks.length} (${chunkLocationCount} locations)...`);

        const chunkResponse = await axiosWithTimeout.post(
          `/api/wayfinder/categories/bulk-import-with-locations/${userId}/chunk`,
          {
            sessionId,
            chunkIndex: i,
            categoriesWithLocations: chunks[i],
          }
        );

        console.log(`✅ Chunk ${i + 1}/${chunks.length} processed: ${chunkLocationCount} locations`);

        // Merge chunk results
        const chunkData = chunkResponse.data.chunkResults;
        combinedResults.categories.created.push(...chunkData.categories.created);
        combinedResults.categories.skipped.push(...chunkData.categories.skipped);
        combinedResults.categories.errors.push(...chunkData.categories.errors);
        combinedResults.locations.created.push(...chunkData.locations.created);
        combinedResults.locations.skipped.push(...chunkData.locations.skipped);
        combinedResults.locations.errors.push(...chunkData.locations.errors);
        combinedResults.totalCategories += chunkData.totalCategories;
        combinedResults.totalLocations += chunkData.totalLocations;
      }

      // Finalize import
      const finalResponse = await axiosWithTimeout.get(
        `/api/wayfinder/categories/bulk-import-with-locations/${userId}/finalize/${sessionId}`
      );

      console.log('Hierarchical import complete:', finalResponse.data);

      setImportProgress(50);
      setImportStatus('Extracting and importing tags...');

      let tagImportSummary = null;

      // ── 3. Extract tags from enterprise locations and import them ───────────
      const tagMap = {};
      enterpriseLocations.forEach(loc => {
        const locTags = loc.tags || [];
        locTags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : (tag.name || tag);
          if (!tagName || !tagName.trim()) return;
          const key = tagName.trim();
          if (!tagMap[key]) tagMap[key] = [];
          tagMap[key].push({ externalId: loc.externalId || '', name: loc.name?.trim() || '' });
        });
      });

      const tagsWithLocations = Object.entries(tagMap).map(([tagName, locations]) => ({
        tagName,
        locations,
      }));

      if (tagsWithLocations.length > 0) {
        console.log(`Importing ${tagsWithLocations.length} tags with location assignments...`);
        try {
          const tagResponse = await axiosWithTimeout.post(`/api/tags/${userId}/bulk-import-mappedin-tags`, {
            tagsWithLocations,
          });
          if (tagResponse.data.success) {
            tagImportSummary = tagResponse.data.data;
            console.log('Tag import complete:', tagResponse.data.message);
          }
        } catch (tagErr) {
          console.error('Tag import failed (non-fatal):', tagErr);
        }
      } else {
        console.log('No tags found on enterprise locations — skipping tag import.');
      }

      setImportProgress(60);
      setImportStatus('Calculating distances from start location...');

      // ── 4. Auto-calculate distances from start location ─────────────────────
      let distancesPopulated = 0;
      let distanceErrors = 0;
      
      // try {
      //   // Use the selected start location from settings
      //   let startLocation = null;
        
      //   if (settings.mappedInStartLocation) {
      //     // Find by externalId or id from settings
      //     startLocation = enterpriseLocations.find(
      //       loc => loc.externalId === settings.mappedInStartLocation || loc.name === settings.mappedInStartLocationName
      //     );
      //     console.log('Using selected start location from settings:', settings.mappedInStartLocation);
      //   }
        
      //   // Fallback to hardcoded locations if not found (for backward compatibility)
      //   if (!startLocation) {
      //     console.warn('Start location not configured in settings, using fallback for distance calculation');
      //     startLocation = enterpriseLocations.find(
      //       loc => loc.name === 'LOC-1IP30ZO0' || loc.name === 'Holographic Display'
      //     );
      //     if (startLocation) {
      //       console.log('Using fallback start location:', startLocation.name);
      //     }
      //   }

      //   if (startLocation) {
      //     console.log('Calculating distances from start location:', startLocation.name);
          
      //     // Wait a bit to ensure all locations are saved in DB
      //     await new Promise(resolve => setTimeout(resolve, 2000));
          
      //     const locationsToProcess = enterpriseLocations.filter(
      //       loc => loc.externalId && loc.id !== startLocation.id
      //     );
          
      //     for (let i = 0; i < locationsToProcess.length; i++) {
      //       const loc = locationsToProcess[i];
      //       // Calculate progress: 60% to 95% based on actual location processing
      //       const progress = 60 + Math.floor((i / locationsToProcess.length) * 35);
      //       setImportProgress(progress);
      //       setImportStatus(`Calculating distances... (${i + 1}/${locationsToProcess.length})`);
            
      //       try {
      //         const dirs = await mapData.getDirections(startLocation, loc);
      //         if (dirs && dirs.distance != null) {
      //           const distM = Math.round(dirs.distance);
                
      //           // Retry logic for updating distance
      //           let retries = 3;
      //           let updated = false;
                
      //           while (retries > 0 && !updated) {
      //             try {
      //               await axiosWithTimeout.patch(
      //                 `/api/locations/by-external-id/${userId}/${encodeURIComponent(loc.externalId)}`, 
      //                 { distanceFromStart: distM }
      //               );
      //               updated = true;
      //               distancesPopulated++;
      //             } catch (patchErr) {
      //               retries--;
      //               if (retries > 0) {
      //                 // Wait before retry
      //                 await new Promise(resolve => setTimeout(resolve, 1000));
      //               } else {
      //                 console.warn(`Failed to update distance for ${loc.name} (${loc.externalId}) after retries:`, patchErr.message);
      //                 distanceErrors++;
      //               }
      //             }
      //           }
      //         }
      //       } catch (dirErr) {
      //         // Non-fatal — skip this location
      //         console.warn(`Could not calculate directions for ${loc.name}:`, dirErr.message);
      //       }
      //     }
          
      //     console.log(`Distances populated for ${distancesPopulated} locations (${distanceErrors} errors)`);
      //   } else {
      //     console.log('Start location not found — skipping distance calculation');
      //     setImportProgress(95);
      //   }
      // } catch (distErr) {
      //   console.error('Distance calculation failed (non-fatal):', distErr);
      // }

      setImportProgress(100);
      setImportStatus('Import complete!');

      // ── 5. Show results ─────────────────────────────────────────────────────
      const response = { data: { success: true, message: `Hierarchical import completed. Categories: ${combinedResults.categories.created.length} created, ${combinedResults.categories.skipped.length} skipped. Locations: ${combinedResults.locations.created.length} created/updated, ${combinedResults.locations.skipped.length} skipped.`, data: combinedResults } };
      
      if (response.data.success) {
        const combinedResults = {
          ...response.data.data,
          tags: tagImportSummary,
        };
        setHierarchicalImportResults(combinedResults);
        setHierarchicalImportDialogOpen(true);
        
        let successMessage = response.data.message;
        if (tagImportSummary) {
          successMessage += ` + ${tagImportSummary.tagsCreated?.length || 0} tags created, ${tagImportSummary.locationsAssigned?.length || 0} tag assignments made.`;
        }
        if (distancesPopulated > 0) {
          successMessage += ` Distances calculated for ${distancesPopulated} locations.`;
        }
        if (distanceErrors > 0) {
          successMessage += ` (${distanceErrors} distance calculation errors)`;
        }
        
        setMessage({
          type: 'success',
          text: successMessage,
        });
      }
    } catch (error) {
      console.error('Error importing categories with locations:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to import categories with locations from MappedIn',
      });
    } finally {
      setImportingHierarchical(false);
      setImportProgress(0);
      setImportStatus('');
    }
  };

  const handleCloseHierarchicalImportDialog = () => {
    setHierarchicalImportDialogOpen(false);
    setHierarchicalImportResults(null);
  };

  const handleCalculateSteps = async () => {
    if (!settings.mappedInStartLocation) {
      setMessage({ type: 'error', text: 'Please select a Start Location before calculating steps.' });
      return;
    }

    try {
      setCalculatingSteps(true);
      setStepsProgress(0);
      setStepsStatus('Loading MappedIn map data...');
      setMessage({ type: '', text: '' });

      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;
      const mapData = await getMapData(mappedInConfig);

      const enterpriseLocations = mapData.getByType('enterprise-location');

      console.log("Enterprise Locations", enterpriseLocations)

      // Find the start location
      const startLoc = enterpriseLocations.find(
        loc => loc.externalId === settings.mappedInStartLocation || loc.name === settings.mappedInStartLocationName
      );

      if (!startLoc) {
        setMessage({ type: 'error', text: `Start location "${settings.mappedInStartLocationName}" not found in MappedIn map data.` });
        setCalculatingSteps(false);
        return;
      }

      // Filter destinations (all except start)
      const destinations = enterpriseLocations.filter(
        loc => loc.externalId && loc.id !== startLoc.id
      );

      setStepsStatus(`Calculating steps for ${destinations.length} locations...`);

      const CHUNK_SIZE = 10;
      const results = { updated: [], failed: [], skipped: [] };

      for (let i = 0; i < destinations.length; i += CHUNK_SIZE) {
        const chunk = destinations.slice(i, i + CHUNK_SIZE);
        const progress = Math.round(((i + chunk.length) / destinations.length) * 100);
        setStepsProgress(progress);
        setStepsStatus(`Processing ${Math.min(i + CHUNK_SIZE, destinations.length)} / ${destinations.length} locations...`);

        await Promise.all(
          chunk.map(async loc => {
            try {
              // Use fetchStepsFromMappedIn for consistent, human-readable step text
              const result = await fetchStepsFromMappedIn(loc.externalId, loc.name, userId);

              // Also get distance from raw directions
              const dirs = await mapData.getDirections(startLoc, loc);
              const distance = dirs?.distance != null ? Math.round(dirs.distance) : null;

              await axios.patch(
                `/api/locations/by-external-id/${userId}/${encodeURIComponent(loc.externalId)}`,
                {
                  totalSteps: result.totalSteps,
                  stepsFetched: true,
                  stepDetails: result.steps,
                  ...(distance !== null ? { distanceFromStart: distance } : {}),
                }
              );

              results.updated.push({ name: loc.name, externalId: loc.externalId, steps: result.totalSteps, distance });
            } catch (err) {
              if (err.message?.includes('No directions') || err.message?.includes('not found')) {
                results.skipped.push({ name: loc.name, externalId: loc.externalId, reason: err.message });
              } else {
                results.failed.push({ name: loc.name, externalId: loc.externalId, error: err.message });
              }
            }
          })
        );
      }

      setStepsProgress(100);
      setStepsStatus('Done!');
      setStepsResults(results);
      setStepsDialogOpen(true);
      setMessage({
        type: 'success',
        text: `Steps calculated: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed.`,
      });
    } catch (err) {
      console.error('Error calculating steps:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to calculate steps.' });
    } finally {
      setCalculatingSteps(false);
      setStepsProgress(0);
      setStepsStatus('');
    }
  };

  const handleImportStepsForLocations = async () => {
    if (!settings.mappedInStartLocation) {
      setMessage({ type: 'error', text: 'Please select a Start Location before importing steps.' });
      return;
    }

    try {
      setImportingSteps(true);
      setImportStepsProgress(0);
      setImportStepsStatus('Loading MappedIn map data...');
      setMessage({ type: '', text: '' });

      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;
      const mapData = await getMapData(mappedInConfig);

      const enterpriseLocations = mapData.getByType('enterprise-location');

      // Find start location
      const startLoc = enterpriseLocations.find(
        loc =>
          loc.externalId === settings.mappedInStartLocation ||
          loc.name === settings.mappedInStartLocationName
      );

      if (!startLoc) {
        setMessage({
          type: 'error',
          text: `Start location "${settings.mappedInStartLocationName}" not found in MappedIn map data.`,
        });
        setImportingSteps(false);
        return;
      }

      const destinations = enterpriseLocations.filter(
        loc => loc.externalId && loc.id !== startLoc.id
      );

      setImportStepsStatus(`Processing ${destinations.length} locations in chunks...`);

      const CHUNK_SIZE = 10;
      const results = { updated: [], failed: [], skipped: [] };

      for (let i = 0; i < destinations.length; i += CHUNK_SIZE) {
        const chunk = destinations.slice(i, i + CHUNK_SIZE);
        const processed = Math.min(i + CHUNK_SIZE, destinations.length);
        const progress = Math.round((processed / destinations.length) * 100);

        setImportStepsProgress(progress);
        setImportStepsStatus(
          `Chunk ${Math.ceil((i + 1) / CHUNK_SIZE)} / ${Math.ceil(destinations.length / CHUNK_SIZE)} — processing ${processed} / ${destinations.length} locations...`
        );

        await Promise.all(
          chunk.map(async loc => {
            try {
              // Use fetchStepsFromMappedIn for consistent, human-readable step text
              const result = await fetchStepsFromMappedIn(loc.externalId, loc.name, userId);

              // Also get distance from raw directions
              const dirs = await mapData.getDirections(startLoc, loc);
              const distance = dirs?.distance != null ? Math.round(dirs.distance) : null;

              await axios.patch(
                `/api/locations/by-external-id/${userId}/${encodeURIComponent(loc.externalId)}`,
                {
                  totalSteps: result.totalSteps,
                  stepsFetched: true,
                  stepDetails: result.steps,
                  ...(distance !== null ? { distanceFromStart: distance } : {}),
                }
              );

              results.updated.push({ name: loc.name, externalId: loc.externalId, steps: result.totalSteps, distance });
            } catch (err) {
              if (err.message?.includes('No directions') || err.message?.includes('not found')) {
                results.skipped.push({ name: loc.name, externalId: loc.externalId, reason: err.message });
              } else {
                results.failed.push({ name: loc.name, externalId: loc.externalId, error: err.message });
              }
            }
          })
        );
      }

      setImportStepsProgress(100);
      setImportStepsStatus('Done!');
      setImportStepsResults(results);
      setImportStepsDialogOpen(true);
      setMessage({
        type: 'success',
        text: `Steps imported: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed.`,
      });
    } catch (err) {
      console.error('Error importing steps:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to import steps.' });
    } finally {
      setImportingSteps(false);
      setImportStepsProgress(0);
      setImportStepsStatus('');
    }
  };

  const handleGenerateTextDirections = async () => {
    if (!settings.mappedInStartLocation) {
      setMessage({ type: 'error', text: 'Please select a Start Location before generating text directions.' });
      return;
    }

    try {
      setGeneratingTextDirections(true);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('Fetching locations from database...');
      setMessage({ type: '', text: '' });

      // Fetch all locations for this user that have an externalId but empty textDirections
      const locationsRes = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      if (!locationsRes.data.success) {
        throw new Error('Failed to fetch locations');
      }

      const allLocations = locationsRes.data.data || [];
      // Only process locations that have an externalId and empty/missing English text directions
      const toProcess = allLocations.filter(loc => {
        if (!loc.externalId) return false;
        const enText = loc.textDirectionsMultiLang?.en || loc.textDirections || '';
        return !enText.trim();
      });

      if (toProcess.length === 0) {
        setMessage({ type: 'success', text: 'All locations already have text directions. Nothing to generate.' });
        setGeneratingTextDirections(false);
        return;
      }

      // Show video generation modal
      setGeneratingTextDirections(false);
      setVideoGenModal({ open: true, locationsToProcess: toProcess });
    } catch (err) {
      console.error('Error fetching locations:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to fetch locations.' });
      setGeneratingTextDirections(false);
    }
  };

  // Handler for "Generate Now" option
  const handleGenerateNowWithVideos = async () => {
    setVideoGenModal((m) => ({ ...m, open: false }));
    const toProcess = videoGenModal.locationsToProcess;

    try {
      setGeneratingTextDirections(true);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus(`Generating text directions for ${toProcess.length} locations...`);
      setMessage({ type: '', text: '' });

      console.log('🚀 Starting handleGenerateNowWithVideos with', toProcess.length, 'locations');

      // Use bulk processing - loads map ONCE for all locations
      setTextDirectionsStatus('Loading map data (one-time load)...');
      const allResults = await bulkGetDirectionsFromStart(
        userId,
        settings.mappedInStartLocation,
        settings.mappedInStartLocationName,
        toProcess
      );

      console.log('✅ Bulk directions completed, now saving to database...');

      const results = { updated: [], failed: [], skipped: [] };
      const CHUNK_SIZE = 5; // Process 5 saves at a time
      
      for (let i = 0; i < allResults.length; i += CHUNK_SIZE) {
        const chunk = allResults.slice(i, i + CHUNK_SIZE);
        const processed = Math.min(i + CHUNK_SIZE, allResults.length);
        const progress = Math.round((processed / allResults.length) * 100);

        setTextDirectionsProgress(progress);
        setTextDirectionsStatus(`Saving ${processed} / ${allResults.length} locations...`);

        await Promise.all(
          chunk.map(async (result) => {
            if (!result.success) {
              results.skipped.push({ 
                name: result.name, 
                externalId: result.externalId, 
                reason: result.error 
              });
              return;
            }

            try {
              // Save via update-text-and-regenerate (triggers video generation)
              await axios.put(
                `/api/wayfinder/locations/${result._id}/update-text-and-regenerate`,
                {
                  textDirections: result.text,
                  currentLanguage: 'en',
                }
              );

              results.updated.push({ name: result.name, externalId: result.externalId });
            } catch (err) {
              console.error(`Failed to save location ${result.name}:`, err);
              results.failed.push({ name: result.name, externalId: result.externalId, error: err.message });
            }
          })
        );
      }

      setTextDirectionsProgress(100);
      setTextDirectionsStatus('Done! Videos are being generated...');
      setTextDirectionsResults(results);
      setTextDirectionsDialogOpen(true);
      setMessage({
        type: 'success',
        text: `Text directions generated: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed. Videos are being generated in the background.`,
      });
    } catch (err) {
      console.error('Error generating text directions:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to generate text directions.' });
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('');
    } finally {
      setGeneratingTextDirections(false);
    }
  };

  // Handler for "Schedule Later" option
  const handleScheduleLaterWithVideos = async () => {
    if (!scheduleDate || !scheduleTime) {
      setMessage({ type: 'error', text: 'Please select date and time' });
      return;
    }

    setIsScheduling(true);
    const toProcess = videoGenModal.locationsToProcess;
    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();

    try {
      setTextDirectionsProgress(0);
      setTextDirectionsStatus(`Scheduling text directions for ${toProcess.length} locations...`);
      setMessage({ type: '', text: '' });

      console.log('🚀 Starting handleScheduleLaterWithVideos with', toProcess.length, 'locations');

      // Use bulk processing - loads map ONCE for all locations
      setTextDirectionsStatus('Loading map data (one-time load)...');
      const allResults = await bulkGetDirectionsFromStart(
        userId,
        settings.mappedInStartLocation,
        settings.mappedInStartLocationName,
        toProcess
      );

      console.log('✅ Bulk directions completed, now scheduling...');

      const results = { updated: [], failed: [], skipped: [] };
      const CHUNK_SIZE = 5; // Process 5 saves at a time
      
      for (let i = 0; i < allResults.length; i += CHUNK_SIZE) {
        const chunk = allResults.slice(i, i + CHUNK_SIZE);
        const processed = Math.min(i + CHUNK_SIZE, allResults.length);
        const progress = Math.round((processed / allResults.length) * 100);

        setTextDirectionsProgress(progress);
        setTextDirectionsStatus(`Scheduling ${processed} / ${allResults.length} locations...`);

        await Promise.all(
          chunk.map(async (result) => {
            if (!result.success) {
              results.skipped.push({ 
                name: result.name, 
                externalId: result.externalId, 
                reason: result.error 
              });
              return;
            }

            try {
              // Schedule video generation
              await axios.post(`/api/wayfinder/locations/${result._id}/schedule-video-generation`, {
                textDirections: result.text,
                language: 'en',
                scheduledDateTime: scheduledDateTime,
              });

              results.updated.push({ name: result.name, externalId: result.externalId });
            } catch (err) {
              results.failed.push({ name: result.name, externalId: result.externalId, error: err.message });
            }
          })
        );
      }

      setTextDirectionsProgress(100);
      setTextDirectionsStatus('Done!');
      setTextDirectionsResults(results);
      setTextDirectionsDialogOpen(true);
      setVideoGenModal((m) => ({ ...m, open: false }));
      setScheduleDate('');
      setScheduleTime('');
      setMessage({
        type: 'success',
        text: `Text directions scheduled: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed. Videos will be generated at the scheduled time.`,
      });
    } catch (err) {
      console.error('Error scheduling text directions:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to schedule text directions.' });
    } finally {
      setIsScheduling(false);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('');
    }
  };

  // Handler for "Save Script Only" option
  const handleSaveScriptOnly = async () => {
    setVideoGenModal((m) => ({ ...m, open: false }));
    const toProcess = videoGenModal.locationsToProcess;

    try {
      setGeneratingTextDirections(true);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus(`Saving text directions for ${toProcess.length} locations...`);
      setMessage({ type: '', text: '' });

      console.log('🚀 Starting handleSaveScriptOnly with', toProcess.length, 'locations');

      // Use bulk processing - loads map ONCE for all locations
      setTextDirectionsStatus('Loading map data (one-time load)...');
      const allResults = await bulkGetDirectionsFromStart(
        userId,
        settings.mappedInStartLocation,
        settings.mappedInStartLocationName,
        toProcess
      );

      console.log('✅ Bulk directions completed, now saving scripts...');

      const results = { updated: [], failed: [], skipped: [] };
      const CHUNK_SIZE = 5; // Process 5 saves at a time
      
      for (let i = 0; i < allResults.length; i += CHUNK_SIZE) {
console.log("i", i, i + CHUNK_SIZE)

        const chunk = allResults.slice(i, i + CHUNK_SIZE);
        const processed = Math.min(i + CHUNK_SIZE, allResults.length);
        const progress = Math.round((processed / allResults.length) * 100);

        console.log("chunks", chunk, allResults)

        setTextDirectionsProgress(progress);
        setTextDirectionsStatus(`Saving ${processed} / ${allResults.length} locations...`);

        await Promise.all(
          chunk.map(async (result) => {
            if (!result.success) {
              // Handle name being either string or object
              const locationName = typeof result.name === 'object' ? (result.name.en || result.name) : result.name;
              results.skipped.push({ 
                name: locationName, 
                externalId: result.externalId, 
                reason: result.error 
              });
              return;
            }

            console.log("result", result)

            try {
              // Save text directions only (no video generation)
              // Update textDirectionsMultiLang.en instead of textDirections
              await axios.patch(
                `/api/locations/by-external-id/${userId}/${encodeURIComponent(result.externalId)}`,
                { 
                  textDirectionsMultiLang: {
                    en: result.text
                  }
                }
              );

              // Handle name being either string or object
              const locationName = typeof result.name === 'object' ? (result.name.en || result.name) : result.name;
              results.updated.push({ name: locationName, externalId: result.externalId });
            } catch (err) {
              // Handle name being either string or object
              const locationName = typeof result.name === 'object' ? (result.name.en || result.name) : result.name;
              results.failed.push({ name: locationName, externalId: result.externalId, error: err.message });
            }
          })
        );
      }

      setTextDirectionsProgress(100);
      setTextDirectionsStatus('Done!');
      setTextDirectionsResults(results);
      setTextDirectionsDialogOpen(true);
      setMessage({
        type: 'success',
        text: `Text directions saved: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed. No videos were generated.`,
      });
    } catch (err) {
      console.error('Error saving text directions:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to save text directions.' });
    } finally {
      setGeneratingTextDirections(false);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('');
    }
  };

  const handleImportTagsWithLocations = async () => {    try {
      setImportingTags(true);
      setMessage({ type: '', text: '' });

      // Load MappedIn SDK and fetch enterprise locations with their tags
      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;
      const mapData = await getMapData(mappedInConfig);

      const enterpriseLocations = mapData.getByType('enterprise-location');

      // Build a map: tagName → [{ externalId, name }]
      const tagMap = {};
      enterpriseLocations.forEach(loc => {
        const locTags = loc.tags || [];
        locTags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : tag.name || tag;
          if (!tagName) return;
          if (!tagMap[tagName]) tagMap[tagName] = [];
          tagMap[tagName].push({ externalId: loc.externalId || '', name: loc.name || '' });
        });
      });

      const tagsWithLocations = Object.entries(tagMap).map(([tagName, locations]) => ({
        tagName,
        locations,
      }));

      if (tagsWithLocations.length === 0) {
        setMessage({ type: 'error', text: 'No tags found on enterprise locations in MappedIn.' });
        setImportingTags(false);
        return;
      }

      console.log('Importing tags with locations:', tagsWithLocations);

      const response = await axios.post(`/api/tags/${userId}/bulk-import-mappedin-tags`, {
        tagsWithLocations,
      });

      if (response.data.success) {
        setTagImportResults(response.data.data);
        setTagImportDialogOpen(true);
        setMessage({ type: 'success', text: response.data.message });
      }
    } catch (error) {
      console.error('Error importing tags:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to import tags from MappedIn' });
    } finally {
      setImportingTags(false);
    }
  };

  const handleCloseTagImportDialog = () => {
    setTagImportDialogOpen(false);
    setTagImportResults(null);
  };

  const handleBulkFillTextDirections = async () => {
    if (!settings.mappedInStartLocation) {
      setMessage({ type: 'error', text: 'Please select a Start Location before filling text directions.' });
      return;
    }

    try {
      setImportingTextDirections(true);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('Fetching locations with empty text directions...');
      setMessage({ type: '', text: '' });

      // Get all locations for this user
      const locationsRes = await axios.get(`/api/locations/${userId}`);
      if (!locationsRes.data.success) {
        throw new Error('Failed to fetch locations');
      }

      // Filter to only locations that have an externalId but empty/missing en text directions
      const allLocations = locationsRes.data.data || [];
      const emptyLocations = allLocations.filter(loc => {
        if (!loc.externalId) return false;
        const enText = loc.textDirectionsMultiLang?.en || loc.textDirections || '';
        return !enText.trim();
      });

      if (emptyLocations.length === 0) {
        setMessage({ type: 'success', text: 'All locations already have text directions filled in.' });
        setImportingTextDirections(false);
        return;
      }

      setTextDirectionsStatus(`Found ${emptyLocations.length} locations with empty text directions. Processing...`);

      console.log('🚀 Starting handleBulkFillTextDirections with', emptyLocations.length, 'locations');

      // Use bulk processing - loads map ONCE for all locations
      setTextDirectionsStatus('Loading map data (one-time load)...');
      const allResults = await bulkGetDirectionsFromStart(
        userId,
        settings.mappedInStartLocation,
        settings.mappedInStartLocationName,
        emptyLocations
      );

      console.log('✅ Bulk directions completed, now saving...');

      const results = { updated: [], failed: [], skipped: [] };
      const CHUNK_SIZE = 5; // Process 5 saves at a time
      
      for (let i = 0; i < allResults.length; i += CHUNK_SIZE) {
        const chunk = allResults.slice(i, i + CHUNK_SIZE);
        const processed = Math.min(i + CHUNK_SIZE, allResults.length);
        const progress = Math.round((processed / allResults.length) * 100);

        setTextDirectionsProgress(progress);
        setTextDirectionsStatus(`Saving ${processed} / ${allResults.length} locations...`);

        await Promise.all(
          chunk.map(async (result) => {
            if (!result.success) {
              results.skipped.push({ 
                name: result.name, 
                externalId: result.externalId, 
                reason: result.error 
              });
              return;
            }

            try {
              await axios.patch(
                `/api/locations/by-external-id/${userId}/${encodeURIComponent(result.externalId)}`,
                { textDirections: result.text }
              );

              results.updated.push({ name: result.name, externalId: result.externalId });
            } catch (err) {
              results.failed.push({ name: result.name, externalId: result.externalId, error: err.message });
            }
          })
        );
      }

      setTextDirectionsProgress(100);
      setTextDirectionsStatus('Done!');
      setTextDirectionsResults(results);
      setTextDirectionsDialogOpen(true);
      setMessage({
        type: 'success',
        text: `Text directions filled: ${results.updated.length} updated, ${results.skipped.length} skipped, ${results.failed.length} failed.`,
      });
    } catch (err) {
      console.error('Error filling text directions:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to fill text directions.' });
    } finally {
      setImportingTextDirections(false);
      setTextDirectionsProgress(0);
      setTextDirectionsStatus('');
    }
  };

  /**
   * Start polling for bulk import status with adaptive polling interval
   */
  const startPollingBulkImport = (jobId) => {
    let pollCount = 0;
    let consecutiveNoChangeCount = 0;
    let lastProgress = 0;
    
    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        const statusRes = await axios.get(`/api/wayfinder/bulk-import/status/${jobId}`);
        
        if (statusRes.data.success) {
          const job = statusRes.data.data;
          
          // Backend progress is 0-100%, map it to 65-100% range (frontend already at 65%)
          const mappedProgress = 65 + Math.floor((job.progress / 100) * 35);
          
          // Track if progress changed
          if (mappedProgress === lastProgress) {
            consecutiveNoChangeCount++;
          } else {
            consecutiveNoChangeCount = 0;
            lastProgress = mappedProgress;
          }
          
          // Update state and localStorage
          setBulkImportProgress(mappedProgress);
          setBulkImportStatus(job.currentStep);
          localStorage.setItem(`bulkImport_${userId}_progress`, mappedProgress.toString());
          localStorage.setItem(`bulkImport_${userId}_status`, job.currentStep);

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId); // Clear timeout to prevent memory leak
            setBulkImportProgress(100);
            setBulkImportResults(job.results);
            setBulkImportDialogOpen(true);
            setBulkImporting(false);
            
            // Clear localStorage
            localStorage.removeItem(`bulkImport_${userId}_active`);
            localStorage.removeItem(`bulkImport_${userId}_jobId`);
            localStorage.removeItem(`bulkImport_${userId}_progress`);
            localStorage.removeItem(`bulkImport_${userId}_status`);
            
            console.log(`✅ Bulk import completed after ${pollCount} status checks`);
            
            setMessage({
              type: 'success',
              text: 'Bulk import completed successfully!',
            });
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId); // Clear timeout to prevent memory leak
            setBulkImporting(false);
            
            // Clear localStorage
            localStorage.removeItem(`bulkImport_${userId}_active`);
            localStorage.removeItem(`bulkImport_${userId}_jobId`);
            localStorage.removeItem(`bulkImport_${userId}_progress`);
            localStorage.removeItem(`bulkImport_${userId}_status`);
            
            console.error(`❌ Bulk import failed after ${pollCount} status checks`);
            
            setMessage({
              type: 'error',
              text: `Bulk import failed: ${job.error || 'Unknown error'}`,
            });
          } else if (consecutiveNoChangeCount >= 10) {
            // If no progress for 10 consecutive checks (50 seconds), slow down polling
            clearInterval(pollInterval);
            clearTimeout(timeoutId); // Clear timeout before switching to slow polling
            console.log(`⏸️ Slowing down polling after ${pollCount} checks (no progress detected)`);
            startSlowPolling(jobId, pollCount);
          }
        }
      } catch (pollErr) {
        console.error('Error polling job status:', pollErr);
        // Don't stop polling on error, backend might be temporarily busy
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup interval after 10 minutes (safety)
    const timeoutId = setTimeout(() => {
      clearInterval(pollInterval);
      if (bulkImporting) {
        setBulkImporting(false);
        localStorage.removeItem(`bulkImport_${userId}_active`);
        localStorage.removeItem(`bulkImport_${userId}_jobId`);
        localStorage.removeItem(`bulkImport_${userId}_progress`);
        localStorage.removeItem(`bulkImport_${userId}_status`);
        console.warn(`⏱️ Bulk import timed out after ${pollCount} status checks`);
        setMessage({ type: 'warning', text: 'Bulk import timed out. Please check the results.' });
      }
    }, 10 * 60 * 1000);
  };

  /**
   * Slower polling for long-running jobs (10 second interval)
   */
  const startSlowPolling = (jobId, initialPollCount) => {
    let pollCount = initialPollCount;
    
    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        const statusRes = await axios.get(`/api/wayfinder/bulk-import/status/${jobId}`);
        
        if (statusRes.data.success) {
          const job = statusRes.data.data;
          
          // Backend progress is 0-100%, map it to 65-100% range
          const mappedProgress = 65 + Math.floor((job.progress / 100) * 35);
          
          // Update state and localStorage
          setBulkImportProgress(mappedProgress);
          setBulkImportStatus(job.currentStep);
          localStorage.setItem(`bulkImport_${userId}_progress`, mappedProgress.toString());
          localStorage.setItem(`bulkImport_${userId}_status`, job.currentStep);

          if (job.status === 'completed') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId); // Clear timeout to prevent memory leak
            setBulkImportProgress(100);
            setBulkImportResults(job.results);
            setBulkImportDialogOpen(true);
            setBulkImporting(false);
            
            // Clear localStorage
            localStorage.removeItem(`bulkImport_${userId}_active`);
            localStorage.removeItem(`bulkImport_${userId}_jobId`);
            localStorage.removeItem(`bulkImport_${userId}_progress`);
            localStorage.removeItem(`bulkImport_${userId}_status`);
            
            console.log(`✅ Bulk import completed after ${pollCount} total status checks (slow polling)`);
            
            setMessage({
              type: 'success',
              text: 'Bulk import completed successfully!',
            });
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            clearTimeout(timeoutId); // Clear timeout to prevent memory leak
            setBulkImporting(false);
            
            // Clear localStorage
            localStorage.removeItem(`bulkImport_${userId}_active`);
            localStorage.removeItem(`bulkImport_${userId}_jobId`);
            localStorage.removeItem(`bulkImport_${userId}_progress`);
            localStorage.removeItem(`bulkImport_${userId}_status`);
            
            console.error(`❌ Bulk import failed after ${pollCount} total status checks (slow polling)`);
            
            setMessage({
              type: 'error',
              text: `Bulk import failed: ${job.error || 'Unknown error'}`,
            });
          }
        }
      } catch (pollErr) {
        console.error('Error polling job status (slow):', pollErr);
      }
    }, 10000); // Poll every 10 seconds for slow polling (reduced frequency)

    // Cleanup after 20 minutes (reduced from 30)
    const timeoutId = setTimeout(() => {
      clearInterval(pollInterval);
      if (bulkImporting) {
        setBulkImporting(false);
        localStorage.removeItem(`bulkImport_${userId}_active`);
        localStorage.removeItem(`bulkImport_${userId}_jobId`);
        localStorage.removeItem(`bulkImport_${userId}_progress`);
        localStorage.removeItem(`bulkImport_${userId}_status`);
        console.warn(`⏱️ Bulk import timed out after ${pollCount} total status checks (slow polling)`);
        setMessage({ type: 'warning', text: 'Bulk import timed out. Please check the results.' });
      }
    }, 20 * 60 * 1000);
  };

  /**
   * Unified bulk import handler - imports everything in one go
   * Uses chunked upload to avoid timeout errors
   */
  const handleUnifiedBulkImport = async () => {
    if (!settings.mappedInStartLocation) {
      setMessage({ type: 'error', text: 'Please select a Start Location before starting bulk import.' });
      return;
    }

    if (!settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId) {
      setMessage({ type: 'error', text: 'Please configure and enable MappedIn settings first.' });
      return;
    }

    try {
      setBulkImporting(true);
      setBulkImportProgress(2);
      setBulkImportStatus('Loading MappedIn data...');
      setMessage({ type: '', text: '' });

      // Step 1: Load MappedIn data on frontend
      const mappedInModule = await import('@mappedin/mappedin-js');
      const { getMapData } = mappedInModule;
      const mapData = await getMapData(mappedInConfig);

      const enterpriseCategories = mapData.getByType('enterprise-category');
      const enterpriseLocations = mapData.getByType('enterprise-location');

      console.log(`Loaded ${enterpriseCategories.length} categories and ${enterpriseLocations.length} locations from MappedIn`);

      // Step 2: Process categories with locations
      setBulkImportProgress(5);
      setBulkImportStatus('Processing categories and locations...');

      const categoriesWithLocations = enterpriseCategories
        .map(category => {
          const sortedLocations = category.locations 
            ? [...category.locations]
                .filter(loc => loc.name && loc.name.trim() !== '')
                .map(loc => ({
                  id: loc.id,
                  name: loc.name.trim(),
                  description: loc.description || '',
                  externalId: loc.externalId || '',
                  floor: loc.floor?.name || loc.floor?.id || '',
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
            : [];

          return {
            id: category.id,
            name: category.name.trim(),
            description: category.description || '',
            externalId: category.externalId || '',
            locations: sortedLocations,
            locationCount: sortedLocations.length,
          };
        })
        .filter(cat => cat.name && cat.name.trim() !== '')
        .sort((a, b) => a.name.localeCompare(b.name));

      // Step 3: Extract tags
      const tagMap = {};
      enterpriseLocations.forEach(loc => {
        const locTags = loc.tags || [];
        locTags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : (tag.name || tag);
          if (!tagName || !tagName.trim()) return;
          const key = tagName.trim();
          if (!tagMap[key]) tagMap[key] = [];
          tagMap[key].push({ externalId: loc.externalId || '', name: loc.name?.trim() || '' });
        });
      });

      const tagsWithLocations = Object.entries(tagMap).map(([tagName, locations]) => ({
        tagName,
        locations,
      }));

      // Step 4: Initialize bulk import job on backend
      setBulkImportProgress(8);
      setBulkImportStatus('Initializing import job...');

      const initResponse = await axios.post('/api/wayfinder/bulk-import/initialize', {
        userId,
        totalCategories: categoriesWithLocations.length,
        totalLocations: enterpriseLocations.length,
        totalTags: tagsWithLocations.length,
        startLocation: settings.mappedInStartLocation,
        startLocationName: settings.mappedInStartLocationName,
      });

      if (!initResponse.data.success) {
        throw new Error(initResponse.data.message || 'Failed to initialize bulk import');
      }

      const jobId = initResponse.data.jobId;
      setBulkImportJobId(jobId);
      
      // Save to localStorage
      localStorage.setItem(`bulkImport_${userId}_active`, 'true');
      localStorage.setItem(`bulkImport_${userId}_jobId`, jobId);

      // Step 5: Upload categories in chunks (to avoid timeout)
      setBulkImportProgress(10);
      setBulkImportStatus('Uploading categories...');

      const CATEGORIES_PER_CHUNK = 20; // Process 20 categories at a time
      const categoryChunks = [];
      for (let i = 0; i < categoriesWithLocations.length; i += CATEGORIES_PER_CHUNK) {
        categoryChunks.push(categoriesWithLocations.slice(i, i + CATEGORIES_PER_CHUNK));
      }

      console.log(`Uploading ${categoryChunks.length} category chunks...`);

      for (let i = 0; i < categoryChunks.length; i++) {
        const progress = 10 + Math.floor((i / categoryChunks.length) * 20); // 10-30%
        setBulkImportProgress(progress);
        setBulkImportStatus(`Uploading categories chunk ${i + 1}/${categoryChunks.length}...`);

        await axios.post('/api/wayfinder/bulk-import/upload-categories', {
          jobId,
          userId,
          categories: categoryChunks[i],
          chunkIndex: i,
          totalChunks: categoryChunks.length,
        });
      }

      // Step 6: Upload tags in chunks to avoid timeout
      setBulkImportProgress(30);
      setBulkImportStatus('Uploading tags...');

      const TAGS_PER_CHUNK = 20; // Process 20 tags at a time
      const tagChunks = [];
      for (let i = 0; i < tagsWithLocations.length; i += TAGS_PER_CHUNK) {
        tagChunks.push(tagsWithLocations.slice(i, i + TAGS_PER_CHUNK));
      }

      console.log(`Uploading ${tagChunks.length} tag chunks...`);

      for (let i = 0; i < tagChunks.length; i++) {
        const progress = 30 + Math.floor((i / tagChunks.length) * 5); // 30-35%
        setBulkImportProgress(progress);
        setBulkImportStatus(`Uploading tags chunk ${i + 1}/${tagChunks.length}...`);

        await axios.post('/api/wayfinder/bulk-import/upload-tags', {
          jobId,
          userId,
          tags: tagChunks[i],
          chunkIndex: i,
          totalChunks: tagChunks.length,
        });
      }

      // Step 7: Calculate steps and distances for all locations
      setBulkImportProgress(35);
      setBulkImportStatus('Calculating steps and distances...');

      // Find start location
      const startLoc = enterpriseLocations.find(
        loc => loc.externalId === settings.mappedInStartLocation || loc.name === settings.mappedInStartLocationName
      );

      const stepsModule = await import('@mappedin/step-by-step/legacy');
      const { getStepByStepInstructions } = stepsModule;

      const DIRECTIONS_EN = {
        "directions.depart-from": "Leave {location}",
        "directions.depart-from-loc": "Leave your location",
        "directions.depart-turn": "Start heading {bearing}",
        "directions.depart-back": "Turn around and start moving",
        "directions.turn": "Turn {bearing}",
        "directions.turn-at": "Turn {bearing} at {location}",
        "directions.turn-at-gate": "Turn {bearing} at Gate {location}",
        "directions.turn-around": "Turn around",
        "directions.continue": "Head {bearing}",
        "directions.continue-straight": "And continue straight",
        "directions.combine": "and",
        "directions.bearing-straight": "straight",
        "directions.bearing-left": "left",
        "directions.bearing-right": "right",
        "directions.bearing-slight-left": "slightly left",
        "directions.bearing-slight-right": "slightly right",
        "directions.take-vortex": "Take {vortex} {direction} to {target}",
        "directions.take-vortex-notarget": "Take {vortex}",
        "directions.take-vortex-noverticality": "Take {vortex} to {target}",
        "directions.vortex-up": "up",
        "directions.vortex-down": "down",
        "directions.vortex-escalator": "escalator",
        "directions.vortex-elevator": "elevator",
        "directions.vortex-stairs": "stairs",
        "directions.vortex-exit-escalator": "Exit escalator",
        "directions.vortex-exit-elevator": "Exit elevator",
        "directions.vortex-exit-stairs": "Exit stairs",
        "directions.arrive-at": "Arrive at {location}",
        "directions.arrive-at-des": "Arrive at your destination",
        "directions.to-loc": "Directions to {location}",
        "directions.to-gate-loc": "Directions to Gate {location}",
        "directions.to-location": "To {location}",
        "directions.towards-location": "Towards {location}",
        "directions.towards-location-gate": "Towards Gate {location}",
        "directions.meters": "meters",
        "directions.feet-abbrev": "ft",
        "less-than-a-minute": "Less than a minute",
        "one-minute": "1 minute",
        min: "{minuteCount} min",
        minutes: "{minuteCount} minutes",
        "minutes-hour": "{hourCount} hr {minuteCount} mins",
        "minutes-hours": "{hourCount} hrs {minuteCount} mins",
        "directions.wait-time-estimated": "Estimated wait time {time} minutes",
        "directions.wait-time-estimated_one": "Estimated wait time 1 minute",
        "directions.wait-time-reason-default": "{reason} required to reach destination",
        "directions.wait-time-reason-security": "Security required to reach destination",
        "directions.wait-time-reason-shuttle": "Shuttle required to reach destination",
        "directions.vortex-exitvortex": "Exit {vortex}",
        "directions.vortex-exit-security": "Go through security",
        "directions.vortex-exit-ramp": "Exit ramp",
        "directions.vortex-door": "door",
        "directions.vortex-ramp": "ramp",
        "directions.vortex-portal": "portal",
        "directions.vortex-security": "security",
        "directions.vortex-shuttle": "shuttle",
        "directions.vortex-slide": "slide",
        "directions.vortex-ladder": "ladder",
      };

      const t = (key, options) => {
        let template = DIRECTIONS_EN[key] || key;
        if (options && typeof options === "object") {
          for (const [k, v] of Object.entries(options)) {
            const val = v != null ? String(v) : "";
            template = template.replace(new RegExp(`\\{${k}\\}`, "g"), val);
          }
        }
        return template;
      };

      const locationsWithSteps = [];
      
      if (startLoc) {
        const destinations = enterpriseLocations.filter(
          loc => loc.externalId && loc.id !== startLoc.id
        );

        for (let i = 0; i < destinations.length; i++) {
          const loc = destinations[i];
          const progress = 35 + Math.floor((i / destinations.length) * 25); // 35-60%
          setBulkImportProgress(progress);
          setBulkImportStatus(`Calculating steps... (${i + 1}/${destinations.length})`);

          try {
            const directions = await mapData.getDirections(startLoc, loc);
            if (directions) {
              const distance = directions.distance != null ? Math.round(directions.distance) : null;
              
              // Get step-by-step instructions
              const instructions = getStepByStepInstructions({
                mapData,
                directions,
                destination: undefined,
                options: {},
                formatOptions: {
                  imperial: false,
                  timeBased: false,
                  language: "en",
                  t,
                },
                locationContext: {},
              });

              if (instructions && instructions.length > 0) {
                locationsWithSteps.push({
                  externalId: loc.externalId,
                  totalSteps: instructions.length,
                  stepDetails: instructions.map((step, index) => ({
                    stepNumber: index + 1,
                    text: step.text,
                    duration: 5,
                  })),
                  distanceFromStart: distance,
                  stepsFetched: true,
                });
              }
            }
          } catch (err) {
            console.warn(`Could not calculate steps for ${loc.name}:`, err.message);
          }
        }
      }

      // Step 8: Upload steps data
      setBulkImportProgress(60);
      setBulkImportStatus('Uploading steps data...');

      await axios.post('/api/wayfinder/bulk-import/upload-steps', {
        jobId,
        userId,
        stepsData: locationsWithSteps,
      });

      // Step 9: Start backend processing
      setBulkImportProgress(65);
      setBulkImportStatus('Starting backend processing...');
      localStorage.setItem(`bulkImport_${userId}_progress`, '65');
      localStorage.setItem(`bulkImport_${userId}_status`, 'Starting backend processing...');

      await axios.post('/api/wayfinder/bulk-import/start-processing', {
        jobId,
      });

      // Step 9: Start polling for job status
      startPollingBulkImport(jobId);

    } catch (err) {
      console.error('Error starting bulk import:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to start bulk import.' });
      setBulkImporting(false);
    }
  };

  const handleImportLocations = async () => {
    try {
      setImporting(true);
      setMessage({ type: '', text: '' });

      if (!mappedInLocations || mappedInLocations.length === 0) {
        setMessage({
          type: 'error',
          text: 'No MappedIn locations found to import. Please check your API settings.',
        });
        setImporting(false);
        return;
      }

      console.log('Checking for existing locations and importing new ones...');

      // Get existing locations from database
      const existingLocationsRes = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      const existingLocations = existingLocationsRes.data.success ? existingLocationsRes.data.data : [];
      
      // Create a Set of existing externalIds for fast lookup
      const existingExternalIds = new Set(
        existingLocations
          .filter(loc => loc.externalId)
          .map(loc => loc.externalId)
      );

      // Filter out locations that already exist
      const newLocations = mappedInLocations.filter(
        loc => loc.externalId && !existingExternalIds.has(loc.externalId)
      );

      if (newLocations.length === 0) {
        setMessage({
          type: 'success',
          text: `All ${mappedInLocations.length} locations are already imported. No new locations to add.`,
        });
        setImporting(false);
        return;
      }

      console.log(`Importing ${newLocations.length} new locations (${mappedInLocations.length - newLocations.length} already exist)`);

      const response = await axios.post(`/api/locations/bulk-import-mappedin/${userId}`, {
        locations: newLocations,
      });

      console.log('Import response:', response.data);

      if (response.data.success) {
        setImportResults({
          ...response.data.data,
          alreadyExisted: mappedInLocations.length - newLocations.length,
        });
        setImportDialogOpen(true);
        setMessage({
          type: 'success',
          text: `${response.data.message} (${mappedInLocations.length - newLocations.length} already existed)`,
        });
      }
    } catch (error) {
      console.error('Error importing locations:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to import locations from MappedIn',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportResults(null);
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setMessage({ type: '', text: '' });

      if (!settings.apiKey || !settings.apiSecret || !settings.mapId) {
        setMessage({
          type: 'error',
          text: 'Please fill in all fields to test connection',
        });
        setTesting(false);
        return;
      }

      console.log('Testing connection with:', {
        apiKey: settings.apiKey.substring(0, 10) + '...',
        apiSecret: settings.apiSecret.substring(0, 10) + '...',
        mapId: settings.mapId,
      });

      const response = await axios.post('/api/wayfinder/mappedin-settings/test-connection', {
        apiKey: settings.apiKey,
        apiSecret: settings.apiSecret,
        mapId: settings.mapId,
      });

      console.log('Test connection response:', response.data);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Connection test successful! Credentials are valid.',
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Connection test failed. Please check your credentials.',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  console.log('Current settings state:', settings);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          MappedIn API Configuration
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure your MappedIn API credentials to enable map functionality. You can get these
          credentials from your MappedIn dashboard.
        </Typography>

        {/* Enable/Disable Toggle */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1">Enable MappedIn Integration:</Typography>
          <ToggleSwitch
            isOn={settings.enabled}
            onChange={handleToggleEnabled}
            id="mappedin-toggle"
          />
        </Box>

        {/* API Key */}
        <TextField
          fullWidth
          label="API Key"
          value={settings.apiKey}
          onChange={e => handleChange('apiKey', e.target.value)}
          placeholder="mik_..."
          type={showApiKey ? 'text' : 'password'}
          disabled={!settings.enabled}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* API Secret */}
        <TextField
          fullWidth
          label="API Secret"
          value={settings.apiSecret}
          onChange={e => handleChange('apiSecret', e.target.value)}
          placeholder="mis_..."
          type={showApiSecret ? 'text' : 'password'}
          disabled={!settings.enabled}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiSecret(!showApiSecret)} edge="end">
                    {showApiSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Map ID */}
        <TextField
          fullWidth
          label="Map ID"
          value={settings.mapId}
          onChange={e => handleChange('mapId', e.target.value)}
          placeholder="Enter your MappedIn Map ID"
          disabled={!settings.enabled}
          sx={{ mb: 3 }}
        />

           {/* MappedIn Link */}
        <TextField
          fullWidth
          label="MappedIn Map Link"
          value={settings.mapLink}
          onChange={e => handleChange('mapLink', e.target.value)}
          placeholder="https://web.mappedin.com/lga-laguardia/laguardia-airport.html#/"
          helperText="Custom iframe URL for the map. If left empty, the default link will be used."
          sx={{ mb: 3 }}
        />

          {/* Start Location Searchable Dropdown */}
        <Autocomplete
          fullWidth
          disabled={!settings.enabled || !mappedInLocations || mappedInLocations.length === 0}
          options={mappedInLocations && mappedInLocations.length > 0 ? [...mappedInLocations].sort((a, b) => a.name.localeCompare(b.name)) : []}
          getOptionLabel={(option) => {
            // Handle both object and string values
            if (typeof option === 'string') return option;
            return option.name || '';
          }}
          value={
            mappedInLocations && mappedInLocations.length > 0 && settings.mappedInStartLocation
              ? mappedInLocations.find(loc => (loc.externalId || loc.id) === settings.mappedInStartLocation) || null
              : null
          }
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false;
            return (option.externalId || option.id) === (value.externalId || value.id);
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              setSettings(prev => ({
                ...prev,
                mappedInStartLocation: newValue.externalId || newValue.id,
                mappedInStartLocationName: newValue.name
              }));
            } else {
              setSettings(prev => ({
                ...prev,
                mappedInStartLocation: '',
                mappedInStartLocationName: ''
              }));
            }
            setMessage({ type: '', text: '' });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Start Location (for distance calculation)"
              placeholder={mappedInLocations && mappedInLocations.length > 0 ? "Search for a location..." : "Loading locations..."}
              helperText={
                (!mappedInLocations || mappedInLocations.length === 0) && settings.enabled 
                  ? 'Save your API credentials first to load locations' 
                  : settings.mappedInStartLocationName 
                    ? `Selected: ${settings.mappedInStartLocationName}` 
                    : ''
              }
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                {option.externalId && (
                  <Typography variant="caption" color="text.secondary">
                    External ID: {option.externalId}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          sx={{ mb: 3 }}
        />

        {/* Use Pointr Map Toggle */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1">Use Pointr Map:</Typography>
          <ToggleSwitch
            isOn={settings.usePointrMap}
            onChange={(isChecked) => handleChange('usePointrMap', isChecked)}
            id="pointr-map-toggle"
          />
        </Box>

        

        {/* Legacy Start Location (kept for backward compatibility) */}
        <TextField
          fullWidth
          label="Start Location ID (Pointer Map)"
          value={settings.startLocation}
          onChange={e => handleChange('startLocation', e.target.value)}
          placeholder="Enter start location ID"
          disabled={!settings.enabled}
          helperText="For Pointer Map"
          sx={{ mb: 3 }}
        />

     

        {/* Message Display */}
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || !settings.enabled}
            sx={{
              backgroundColor: buttonColor,
              '&:hover': {
                backgroundColor: buttonColor,
                opacity: 0.9,
              },
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>

          {/* UNIFIED IMPORT ALL BUTTON */}
          <Button
            variant="contained"
            startIcon={bulkImporting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <CloudDownload />}
            onClick={handleUnifiedBulkImport}
            disabled={bulkImporting || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || !settings.mappedInStartLocation || locationsLoading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
              px: 3,
              py: 1.2,
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
              },
              '&:disabled': {
                background: '#cbd5e1',
                color: '#94a3b8',
              },
            }}
          >
            {bulkImporting ? `Importing All... ${bulkImportProgress}%` : '🚀 Import All (One Click)'}
          </Button>

          {/* <Button
            variant="outlined"
            startIcon={<CheckCircle />}
            onClick={handleTestConnection}
            disabled={testing || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId}
            sx={{
              borderColor: buttonColor,
              color: buttonColor,
              '&:hover': {
                borderColor: buttonColor,
                backgroundColor: `${buttonColor}10`,
              },
            }}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button> */}

          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={handleImportCategories}
            disabled={importingCategories || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || locationsLoading}
            sx={{
              borderColor: '#2196f3',
              color: '#2196f3',
              '&:hover': {
                borderColor: '#2196f3',
                backgroundColor: '#2196f310',
              },
            }}
          >
            {importingCategories ? 'Importing...' : `Import Categories ${mappedInCategories?.length ? `(${mappedInCategories.length})` : ''}`}
          </Button>

          <Button
            variant="outlined"
            startIcon={importingHierarchical ? <CircularProgress size={20} /> : <CloudDownload />}
            onClick={handleImportCategoriesWithLocations}
            disabled={importingHierarchical || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || locationsLoading}
            sx={{
              borderColor: '#9c27b0',
              color: '#9c27b0',
              '&:hover': {
                borderColor: '#9c27b0',
                backgroundColor: '#9c27b010',
              },
            }}
          >
            {importingHierarchical 
              ? `Importing... ${importProgress}%` 
              : `Import/Update Categories + Locations + Tags ${mappedInCategories?.length ? `(${mappedInCategories.length} cats)` : ''}`
            }
          </Button>

          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={handleImportLocations}
            disabled={importing || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || locationsLoading}
            sx={{
              borderColor: '#4caf50',
              color: '#4caf50',
              '&:hover': {
                borderColor: '#4caf50',
                backgroundColor: '#4caf5010',
              },
            }}
          >
            {importing ? 'Importing...' : `Import Locations Only ${mappedInLocations?.length ? `(${mappedInLocations.length})` : ''}`}
          </Button>

          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={handleImportTagsWithLocations}
            disabled={importingTags || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId}
            sx={{
              borderColor: '#ff9800',
              color: '#ff9800',
              '&:hover': {
                borderColor: '#ff9800',
                backgroundColor: '#ff980010',
              },
            }}
          >
            {importingTags ? 'Importing Tags...' : 'Import Tags + Assign to Locations'}
          </Button>

          <Button
            variant="outlined"
            startIcon={calculatingSteps ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            onClick={handleCalculateSteps}
            disabled={calculatingSteps || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || !settings.mappedInStartLocation}
            sx={{
              borderColor: '#00bcd4',
              color: '#00bcd4',
              '&:hover': {
                borderColor: '#00bcd4',
                backgroundColor: '#00bcd410',
              },
            }}
          >
            {calculatingSteps ? `Calculating Steps... ${stepsProgress}%` : 'Calculate Steps (Start → All Locations)'}
          </Button>

          <Button
            variant="outlined"
            startIcon={importingSteps ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={handleImportStepsForLocations}
            disabled={importingSteps || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || !settings.mappedInStartLocation}
            sx={{
              borderColor: '#3f51b5',
              color: '#3f51b5',
              '&:hover': {
                borderColor: '#3f51b5',
                backgroundColor: '#3f51b510',
              },
            }}
          >
            {importingSteps ? `Importing Steps... ${importStepsProgress}%` : 'Import Steps for Locations'}
          </Button>

          <Button
            variant="outlined"
            startIcon={generatingTextDirections ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
            onClick={handleGenerateTextDirections}
            disabled={generatingTextDirections || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || !settings.mappedInStartLocation}
            sx={{
              borderColor: '#e91e63',
              color: '#e91e63',
              '&:hover': {
                borderColor: '#e91e63',
                backgroundColor: '#e91e6310',
              },
            }}
          >
            {generatingTextDirections ? `Generating... ${textDirectionsProgress}%` : 'Generate Text Directions for Videos'}
          </Button>

          <Button
            variant="outlined"
            startIcon={importingTextDirections ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
            onClick={handleBulkFillTextDirections}
            disabled={importingTextDirections || !settings.enabled || !settings.apiKey || !settings.apiSecret || !settings.mapId || !settings.mappedInStartLocation}
            sx={{
              borderColor: '#e91e63',
              color: '#e91e63',
              '&:hover': {
                borderColor: '#e91e63',
                backgroundColor: '#e91e6310',
              },
            }}
          >
            {importingTextDirections ? `Filling Text Directions... ${textDirectionsProgress}%` : 'Fill Empty Text Directions'}
          </Button>

        </Box>

        {/* Import Progress Indicator */}
        {importingHierarchical && importProgress > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#e0e0e0',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${importProgress}%`,
                      height: '100%',
                      backgroundColor: '#9c27b0',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 50 }}>
                <Typography variant="body2" color="text.secondary">
                  {importProgress}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {importStatus}
            </Typography>
          </Box>
        )}


        {/* Steps Calculation Progress */}
        {calculatingSteps && stepsProgress > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#e0f7fa', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${stepsProgress}%`,
                      height: '100%',
                      backgroundColor: '#00bcd4',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 50 }}>
                <Typography variant="body2" color="text.secondary">{stepsProgress}%</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{stepsStatus}</Typography>
          </Box>
        )}

        {/* Import Steps Progress */}
        {importingSteps && importStepsProgress > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8eaf6', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${importStepsProgress}%`,
                      height: '100%',
                      backgroundColor: '#3f51b5',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 50 }}>
                <Typography variant="body2" color="text.secondary">{importStepsProgress}%</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{importStepsStatus}</Typography>
          </Box>
        )}

        {/* Generate Text Directions Progress */}
        {generatingTextDirections && textDirectionsProgress > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#fce4ec', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${textDirectionsProgress}%`,
                      height: '100%',
                      backgroundColor: '#e91e63',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 50 }}>
                <Typography variant="body2" color="text.secondary">{textDirectionsProgress}%</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{textDirectionsStatus}</Typography>
          </Box>
        )}

        {/* Fill Text Directions Progress */}
        {importingTextDirections && textDirectionsProgress > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#fce4ec', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <Box sx={{ width: '100%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${textDirectionsProgress}%`,
                      height: '100%',
                      backgroundColor: '#e91e63',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 50 }}>
                <Typography variant="body2" color="text.secondary">{textDirectionsProgress}%</Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{textDirectionsStatus}</Typography>
          </Box>
        )}

        {/* Unified Bulk Import Progress */}
        {bulkImporting && bulkImportProgress >= 0 && (
          <Box sx={{ mt: 2, p: 3, backgroundColor: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', borderRadius: 2, border: '2px solid #667eea' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: '100%', mr: 2 }}>
                <Box sx={{ width: '100%', height: 12, backgroundColor: '#e0e0e0', borderRadius: 6, overflow: 'hidden', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)' }}>
                  <Box
                    sx={{
                      width: `${bulkImportProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      transition: 'width 0.5s ease',
                      boxShadow: '0 0 10px rgba(102, 126, 234, 0.5)',
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 60 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>{bulkImportProgress}%</Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>{bulkImportStatus}</Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              This may take several minutes. Please don't close this page.
            </Typography>
          </Box>
        )}


        {/* Current Status */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: settings.enabled ? '#e8f5e9' : '#fff3e0', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            Status: {settings.enabled ? '✓ Enabled' : '⚠ Disabled'}
          </Typography>
          {settings.enabled && settings.apiKey && settings.apiSecret && settings.mapId && (
            <Typography variant="caption" color="text.secondary">
              MappedIn integration is active. Maps will use your custom configuration.
            </Typography>
          )}
          {settings.enabled && (!settings.apiKey || !settings.apiSecret || !settings.mapId) && (
            <Typography variant="caption" color="error">
              Please configure all credentials to use MappedIn integration.
            </Typography>
          )}
          {!settings.enabled && (
            <Typography variant="caption" color="text.secondary">
              MappedIn integration is disabled. Enable it to use map functionality.
            </Typography>
          )}
        </Box>

        {/* MappedIn Categories Preview */}
        {settings.enabled && mappedInCategories && mappedInCategories.length > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Available MappedIn Categories ({mappedInCategories.length}):
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              These categories can be imported into your wayfinder system.
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {mappedInCategories.slice(0, 10).map((category, index) => (
                <Chip
                  key={category.id || index}
                  label={category.name}
                  size="small"
                  sx={{ m: 0.5 }}
                  variant="outlined"
                  color="primary"
                />
              ))}
              {mappedInCategories.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ... and {mappedInCategories.length - 10} more
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* MappedIn Locations Preview */}
        {settings.enabled && mappedInLocations && mappedInLocations.length > 0 && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Available MappedIn Locations ({mappedInLocations.length}):
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              These locations can be imported into your wayfinder system.
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {mappedInLocations.slice(0, 10).map((location, index) => (
                <Chip
                  key={location.id || index}
                  label={location.name}
                  size="small"
                  sx={{ m: 0.5 }}
                  variant="outlined"
                />
              ))}
              {mappedInLocations.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ... and {mappedInLocations.length - 10} more
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Locations Loading/Error States */}
        {settings.enabled && locationsLoading && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Loading MappedIn locations...
            </Typography>
          </Box>
        )}

        {settings.enabled && locationsError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {locationsError}
          </Alert>
        )}
      </CardContent>

      {/* Category Import Results Dialog */}
      <Dialog open={categoryImportDialogOpen} onClose={handleCloseCategoryImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            Category Import Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {categoryImportResults && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Import Summary:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Created: ${categoryImportResults.created?.length || 0}`} color="success" />
                <Chip label={`Skipped: ${categoryImportResults.skipped?.length || 0}`} color="warning" />
                <Chip label={`Errors: ${categoryImportResults.errors?.length || 0}`} color="error" />
              </Box>

              {/* Created Categories */}
              {categoryImportResults.created && categoryImportResults.created.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Successfully Created ({categoryImportResults.created.length})
                  </Typography>
                  <List dense>
                    {categoryImportResults.created.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.category.name}
                          secondary={item.category.description || 'No description'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Skipped Categories */}
              {categoryImportResults.skipped && categoryImportResults.skipped.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    ⚠️ Skipped ({categoryImportResults.skipped.length})
                  </Typography>
                  <List dense>
                    {categoryImportResults.skipped.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.category.name}
                          secondary={item.reason}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Error Categories */}
              {categoryImportResults.errors && categoryImportResults.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ❌ Errors ({categoryImportResults.errors.length})
                  </Typography>
                  <List dense>
                    {categoryImportResults.errors.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.category?.name || 'Unknown Category'}
                          secondary={item.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryImportDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Import Results Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            Import Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {importResults && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Import Summary:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Created: ${importResults.created?.length || 0}`} color="success" />
                <Chip label={`Skipped: ${importResults.skipped?.length || 0}`} color="warning" />
                {importResults.alreadyExisted > 0 && (
                  <Chip label={`Already Existed: ${importResults.alreadyExisted}`} color="info" />
                )}
                <Chip label={`Errors: ${importResults.errors?.length || 0}`} color="error" />
              </Box>

              {/* Created Locations */}
              {importResults.created && importResults.created.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Successfully Created ({importResults.created.length})
                  </Typography>
                  <List dense>
                    {importResults.created.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.location.name}
                          secondary={`${item.location.description || 'No description'} ${item.qrCodeGenerated ? '• QR Code Generated' : '• QR Code Failed'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Skipped Locations */}
              {importResults.skipped && importResults.skipped.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    ⚠️ Skipped ({importResults.skipped.length})
                  </Typography>
                  <List dense>
                    {importResults.skipped.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.location.name}
                          secondary={item.reason}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Error Locations */}
              {importResults.errors && importResults.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ❌ Errors ({importResults.errors.length})
                  </Typography>
                  <List dense>
                    {importResults.errors.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.location?.name || 'Unknown Location'}
                          secondary={item.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hierarchical Import Results Dialog */}
      <Dialog open={hierarchicalImportDialogOpen} onClose={handleCloseHierarchicalImportDialog} maxWidth="lg" fullWidth>        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            Hierarchical Import Results (Categories + Locations + Tags)
          </Box>
        </DialogTitle>
        <DialogContent>
          {hierarchicalImportResults && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Import Summary:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Chip label={`Categories Created: ${hierarchicalImportResults.categories?.created?.length || 0}`} color="success" />
                <Chip label={`Categories Skipped: ${hierarchicalImportResults.categories?.skipped?.length || 0}`} color="warning" />
                <Chip label={`Locations Created/Updated: ${hierarchicalImportResults.locations?.created?.length || 0}`} color="success" />
                <Chip label={`Locations Skipped: ${hierarchicalImportResults.locations?.skipped?.length || 0}`} color="warning" />
                <Chip label={`Errors: ${(hierarchicalImportResults.categories?.errors?.length || 0) + (hierarchicalImportResults.locations?.errors?.length || 0)}`} color="error" />
              </Box>

              {/* Categories Section */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" gutterBottom>
                  📁 Categories
                </Typography>

                {/* Created Categories */}
                {hierarchicalImportResults.categories?.created && hierarchicalImportResults.categories.created.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="success.main" gutterBottom>
                      ✅ Successfully Created ({hierarchicalImportResults.categories.created.length})
                    </Typography>
                    <List dense>
                      {hierarchicalImportResults.categories.created.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={item.category.name}
                            secondary={item.category.description || 'No description'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Skipped Categories */}
                {hierarchicalImportResults.categories?.skipped && hierarchicalImportResults.categories.skipped.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="warning.main" gutterBottom>
                      ⚠️ Skipped ({hierarchicalImportResults.categories.skipped.length})
                    </Typography>
                    <List dense>
                      {hierarchicalImportResults.categories.skipped.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={item.category.name}
                            secondary={item.reason}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>

              {/* Locations Section */}
              <Paper sx={{ p: 2, backgroundColor: '#f3e5f5' }}>
                <Typography variant="h6" gutterBottom>
                  📍 Locations
                </Typography>

                {/* Created Locations */}
                {hierarchicalImportResults.locations?.created && hierarchicalImportResults.locations.created.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="success.main" gutterBottom>
                      ✅ Created/Updated ({hierarchicalImportResults.locations.created.length})
                    </Typography>
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {hierarchicalImportResults.locations.created.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${item.updated ? '🔄 ' : '🆕 '}${item.location.name}`}
                            secondary={`Category: ${item.category} ${item.qrCodeGenerated ? '• QR Code Generated' : '• QR Code Failed'} ${item.updated ? '• Updated' : '• Created'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Skipped Locations */}
                {hierarchicalImportResults.locations?.skipped && hierarchicalImportResults.locations.skipped.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="warning.main" gutterBottom>
                      ⚠️ Skipped ({hierarchicalImportResults.locations.skipped.length})
                    </Typography>
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {hierarchicalImportResults.locations.skipped.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={item.location.name}
                            secondary={`Category: ${item.category} • ${item.reason}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Error Locations */}
                {hierarchicalImportResults.locations?.errors && hierarchicalImportResults.locations.errors.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" color="error.main" gutterBottom>
                      ❌ Errors ({hierarchicalImportResults.locations.errors.length})
                    </Typography>
                    <List dense>
                      {hierarchicalImportResults.locations.errors.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={item.location?.name || 'Unknown Location'}
                            secondary={`Category: ${item.category} • ${item.error}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>

              {/* Tags Section */}
              {hierarchicalImportResults.tags && (
                <Paper sx={{ p: 2, mt: 2, backgroundColor: '#fff8e1' }}>
                  <Typography variant="h6" gutterBottom>
                    🏷️ Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Tags Created: ${hierarchicalImportResults.tags.tagsCreated?.length || 0}`} color="success" size="small" />
                    <Chip label={`Tags Reused: ${hierarchicalImportResults.tags.tagsExisted?.length || 0}`} color="info" size="small" />
                    <Chip label={`New Assignments: ${hierarchicalImportResults.tags.locationsAssigned?.length || 0}`} color="primary" size="small" />
                    <Chip label={`Already Set: ${hierarchicalImportResults.tags.locationsAlreadyHadTag?.length || 0}`} color="default" size="small" />
                    {hierarchicalImportResults.tags.locationsNotFound?.length > 0 && (
                      <Chip label={`Locations Not Yet Imported: ${hierarchicalImportResults.tags.locationsNotFound.length}`} color="warning" size="small" />
                    )}
                  </Box>

                  {hierarchicalImportResults.tags.tagsCreated?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        ✅ New Tags Created ({hierarchicalImportResults.tags.tagsCreated.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {hierarchicalImportResults.tags.tagsCreated.map((t, i) => (
                          <Chip key={i} label={t.tagName} size="small" variant="outlined" color="success" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {hierarchicalImportResults.tags.tagsExisted?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="info.main" gutterBottom>
                        ℹ️ Existing Tags Reused ({hierarchicalImportResults.tags.tagsExisted.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {hierarchicalImportResults.tags.tagsExisted.map((t, i) => (
                          <Chip key={i} label={t.tagName} size="small" variant="outlined" color="info" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {hierarchicalImportResults.tags.locationsAssigned?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="primary.main" gutterBottom>
                        🔗 New Tag Assignments ({hierarchicalImportResults.tags.locationsAssigned.length})
                      </Typography>
                      <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {hierarchicalImportResults.tags.locationsAssigned.map((item, i) => (
                          <ListItem key={i} sx={{ py: 0 }}>
                            <ListItemText
                              primary={item.locationName}
                              secondary={`Tag: ${item.tagName}`}
                              primaryTypographyProps={{ fontSize: '13px' }}
                              secondaryTypographyProps={{ fontSize: '12px' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {hierarchicalImportResults.tags.locationsNotFound?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        ⚠️ Locations Not Yet Imported ({hierarchicalImportResults.tags.locationsNotFound.length}) — import locations first
                      </Typography>
                      <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                        {hierarchicalImportResults.tags.locationsNotFound.map((item, i) => (
                          <ListItem key={i} sx={{ py: 0 }}>
                            <ListItemText
                              primary={item.locationName}
                              secondary={`Tag: ${item.tagName}${item.externalId ? ` • ExternalId: ${item.externalId}` : ''}`}
                              primaryTypographyProps={{ fontSize: '13px' }}
                              secondaryTypographyProps={{ fontSize: '12px' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {(!hierarchicalImportResults.tags.tagsCreated?.length && !hierarchicalImportResults.tags.tagsExisted?.length) && (
                    <Typography variant="caption" color="text.secondary">
                      No tags found on enterprise locations in MappedIn.
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHierarchicalImportDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag Import Results Dialog */}
      <Dialog open={tagImportDialogOpen} onClose={handleCloseTagImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            Tag Import Results (MappedIn Tags → Locations)
          </Box>
        </DialogTitle>
        <DialogContent>
          {tagImportResults && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Tags Created: ${tagImportResults.tagsCreated?.length || 0}`} color="success" />
                <Chip label={`Tags Reused: ${tagImportResults.tagsExisted?.length || 0}`} color="info" />
                <Chip label={`New Assignments: ${tagImportResults.locationsAssigned?.length || 0}`} color="primary" />
                <Chip label={`Already Set: ${tagImportResults.locationsAlreadyHadTag?.length || 0}`} color="default" />
                {tagImportResults.locationsNotFound?.length > 0 && (
                  <Chip label={`Not Yet Imported: ${tagImportResults.locationsNotFound.length}`} color="warning" />
                )}
                <Chip label={`Errors: ${tagImportResults.errors?.length || 0}`} color="error" />
              </Box>

              {tagImportResults.tagsCreated?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ New Tags Created ({tagImportResults.tagsCreated.length})
                  </Typography>
                  <List dense>
                    {tagImportResults.tagsCreated.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.tagName} secondary={`ID: ${item.tagId}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {tagImportResults.tagsExisted?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="info.main" gutterBottom>
                    ℹ️ Existing Tags Reused ({tagImportResults.tagsExisted.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {tagImportResults.tagsExisted.map((item, i) => (
                      <Chip key={i} label={item.tagName} size="small" variant="outlined" color="info" />
                    ))}
                  </Box>
                </Box>
              )}

              {tagImportResults.locationsAssigned?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    🔗 New Tag Assignments ({tagImportResults.locationsAssigned.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {tagImportResults.locationsAssigned.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={item.locationName}
                          secondary={`Tag: ${item.tagName}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {tagImportResults.locationsNotFound?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    ⚠️ Locations Not Yet Imported ({tagImportResults.locationsNotFound.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    These locations exist in MappedIn but haven't been imported into your DB yet. Import locations first, then re-run tag import.
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {tagImportResults.locationsNotFound.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={item.locationName}
                          secondary={`Tag: ${item.tagName}${item.externalId ? ` • ExternalId: ${item.externalId}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {tagImportResults.errors?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ❌ Errors ({tagImportResults.errors.length})
                  </Typography>
                  <List dense>
                    {tagImportResults.errors.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.tagName || item.location} secondary={item.error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTagImportDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import Steps Results Dialog */}
      <Dialog open={importStepsDialogOpen} onClose={() => setImportStepsDialogOpen(false)} maxWidth="md" fullWidth>        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download />
            Import Steps Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {importStepsResults && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Updated: ${importStepsResults.updated?.length || 0}`} color="success" />
                <Chip label={`Skipped: ${importStepsResults.skipped?.length || 0}`} color="warning" />
                <Chip label={`Failed: ${importStepsResults.failed?.length || 0}`} color="error" />
              </Box>

              {importStepsResults.updated?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Updated ({importStepsResults.updated.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {importStepsResults.updated.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={item.name}
                          secondary={`Steps: ${item.steps}${item.distance != null ? ` • Distance: ${item.distance}m` : ''} • ExternalId: ${item.externalId}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {importStepsResults.skipped?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    ⚠️ Skipped ({importStepsResults.skipped.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importStepsResults.skipped.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.name} secondary={item.reason} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {importStepsResults.failed?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ❌ Failed ({importStepsResults.failed.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importStepsResults.failed.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.name} secondary={item.error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportStepsDialogOpen(false); setImportStepsResults(null); }} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Fill Text Directions Results Dialog */}
      <Dialog open={textDirectionsDialogOpen} onClose={() => setTextDirectionsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudDownload />
            Fill Empty Text Directions — Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {textDirectionsResults && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Updated: ${textDirectionsResults.updated?.length || 0}`} color="success" />
                <Chip label={`Skipped: ${textDirectionsResults.skipped?.length || 0}`} color="warning" />
                <Chip label={`Failed: ${textDirectionsResults.failed?.length || 0}`} color="error" />
              </Box>

              {textDirectionsResults.updated?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Updated ({textDirectionsResults.updated.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {textDirectionsResults.updated.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.name} secondary={`ExternalId: ${item.externalId}`} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {textDirectionsResults.skipped?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    ⚠️ Skipped ({textDirectionsResults.skipped.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {textDirectionsResults.skipped.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.name} secondary={item.reason} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {textDirectionsResults.failed?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ❌ Failed ({textDirectionsResults.failed.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {textDirectionsResults.failed.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={item.name} secondary={item.error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTextDirectionsDialogOpen(false); setTextDirectionsResults(null); }} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Generation Options Modal */}
      <Dialog
        open={videoGenModal.open}
        onClose={() => setVideoGenModal((m) => ({ ...m, open: false }))}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.4)' } } }}
        PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc', textAlign: 'center', py: 3, px: 4, position: 'relative'
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.3px', mb: 0.5, color: "#fff" }}>
            Video Generation Options
          </Typography>
          <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: 13, background: 'rgba(255,255,255,0.1)', padding: '2px 10px', borderRadius: 20 }}>
            {videoGenModal.locationsToProcess?.length || 0} location(s) ready for text direction generation
          </Typography>
          <IconButton
            onClick={() => setVideoGenModal((m) => ({ ...m, open: false }))}
            sx={{ position: 'absolute', right: 16, top: 16, color: '#94a3b8', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3.5, pb: 1, px: 4, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Generate Now */}
            <Button
              variant="contained"
              fullWidth
              startIcon={<FlashOnIcon sx={{ fontSize: 22 }} />}
              onClick={handleGenerateNowWithVideos}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', textTransform: 'none', fontWeight: 800, borderRadius: '14px', py: 1.8,
                fontSize: 16, color: '#fff', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(16, 185, 129, 0.4)' },
              }}
            >
              Generate Now
            </Button>

            {/* Schedule Option */}
            <Paper
              elevation={0}
              sx={{
                p: 3, border: '2px solid #e2e8f0', borderRadius: '16px', background: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': { borderColor: '#93c5fd', background: '#f0f9ff', boxShadow: '0 8px 24px rgba(59, 130, 246, 0.08)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ p: 1, borderRadius: '10px', background: '#e0f2fe', color: '#0284c7' }}>
                  <ScheduleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Schedule for Later</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Date</Typography>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Time</Typography>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={handleScheduleLaterWithVideos}
                disabled={isScheduling || !scheduleDate || !scheduleTime}
                startIcon={isScheduling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ScheduleIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', textTransform: 'none', fontWeight: 700, borderRadius: '10px', py: 1.2,
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
                  '&:disabled': { background: '#cbd5e1', color: '#f8fafc' },
                }}
              >
                {isScheduling ? 'Scheduling…' : 'Confirm Schedule'}
              </Button>
            </Paper>

            {/* Cancel (Save Script Only) */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="text"
                startIcon={<Save sx={{ fontSize: 16 }} />}
                onClick={handleSaveScriptOnly}
                sx={{
                  color: '#64748b', textTransform: 'none', fontWeight: 600, fontSize: 14, px: 3, py: 1, borderRadius: 8,
                  '&:hover': { color: '#0f172a', background: '#f1f5f9' },
                }}
              >
                Just save script, skip video for now
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, bgcolor: '#f8fafc' }}>
          {/* Empty DialogActions simply to add spacing at the bottom or allow future actions */}
        </DialogActions>
      </Dialog>

      {/* Unified Bulk Import Results Dialog */}
      <Dialog open={bulkImportDialogOpen} onClose={() => setBulkImportDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#10b981' }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Bulk Import Complete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {bulkImportResults && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Import Summary
              </Typography>

              {/* Helper function to safely get count */}
              {(() => {
                const getCount = (value) => {
                  if (typeof value === 'number') return value;
                  if (Array.isArray(value)) return value.length;
                  if (value && typeof value === 'object') return Object.keys(value).length;
                  return 0;
                };

                return (
                  <>
                    {/* Categories */}
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e3f2fd' }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        📁 Categories
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Created: ${getCount(bulkImportResults.categories?.created)}`} color="success" size="small" />
                        <Chip label={`Skipped: ${getCount(bulkImportResults.categories?.skipped)}`} color="default" size="small" />
                        <Chip label={`Errors: ${getCount(bulkImportResults.categories?.errors)}`} color="error" size="small" />
                      </Box>
                    </Paper>

                    {/* Locations */}
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f3e5f5' }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        📍 Locations
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Created: ${getCount(bulkImportResults.locations?.created)}`} color="success" size="small" />
                        <Chip label={`Updated: ${getCount(bulkImportResults.locations?.updated)}`} color="info" size="small" />
                        <Chip label={`Skipped: ${getCount(bulkImportResults.locations?.skipped)}`} color="default" size="small" />
                        <Chip label={`Errors: ${getCount(bulkImportResults.locations?.errors)}`} color="error" size="small" />
                      </Box>
                    </Paper>

                    {/* Tags */}
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#fff8e1' }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        🏷️ Tags
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Created: ${getCount(bulkImportResults.tags?.created)}`} color="success" size="small" />
                        <Chip label={`Assigned: ${getCount(bulkImportResults.tags?.assigned)}`} color="primary" size="small" />
                        <Chip label={`Errors: ${getCount(bulkImportResults.tags?.errors)}`} color="error" size="small" />
                      </Box>
                    </Paper>

                    {/* Steps */}
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#e0f7fa' }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        👣 Steps Calculated
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Calculated: ${getCount(bulkImportResults.steps?.calculated)}`} color="success" size="small" />
                        <Chip label={`Skipped: ${getCount(bulkImportResults.steps?.skipped)}`} color="default" size="small" />
                        <Chip label={`Errors: ${getCount(bulkImportResults.steps?.errors)}`} color="error" size="small" />
                      </Box>
                    </Paper>

                    {/* Text Directions */}
                    <Paper sx={{ p: 2, backgroundColor: '#fce4ec' }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        📝 Text Directions Generated
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Generated: ${getCount(bulkImportResults.textDirections?.generated)}`} color="success" size="small" />
                        <Chip label={`Skipped: ${getCount(bulkImportResults.textDirections?.skipped)}`} color="default" size="small" />
                        <Chip label={`Errors: ${getCount(bulkImportResults.textDirections?.errors)}`} color="error" size="small" />
                      </Box>
                    </Paper>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkImportDialogOpen(false)} variant="contained" sx={{ bgcolor: buttonColor }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MapSettings;
